import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const PLANS = [
  {
    name: 'Starter',
    monthlyPrice: 10,
    description: 'Perfect for getting started',
    features: [
      '1M tokens per month',
      '4 AI models (GPT-4o mini, Gemini 2.5 Flash + more)',
      'PDF Analyzer & Vision tools',
      'AI Assistants',
    ],
    isPopular: false,
    trial: null,
  },
  {
    name: 'Pro',
    monthlyPrice: 23.85,
    description: 'Try free for 14 days — cancel anytime',
    features: [
      '2.2M tokens per month',
      '16 models (GPT-4o, Claude Sonnet + more)',
      'Custom AI Assistants',
    ],
    isPopular: true,
    trial: { days: 14, price: 2.85 },
  },
  {
    name: 'Premium',
    monthlyPrice: 97,
    description: 'Maximum access, no limits',
    features: [
      'Unlimited tokens per month',
      'All 18 models incl. Claude Opus 4.6',
      'Priority support & early access',
      'Highest per-model token limits',
    ],
    isPopular: false,
    trial: null,
  },
];

const getYearlyPrice = (p) => Math.round(p * 12 * 0.8 * 100) / 100;
const formatPrice = (p) => p % 1 === 0 ? `$${p}` : `$${p.toFixed(2)}`;

const PricingSection = () => {
  const [billing, setBilling] = useState('yearly');
  const { isDark } = useTheme();

  const accent = isDark ? '#28F5AB' : '#177755';
  const accentBg = isDark ? 'rgba(40,245,171,0.10)' : 'rgba(23,119,85,0.07)';
  const accentBorder = isDark ? 'rgba(40,245,171,0.25)' : 'rgba(23,119,85,0.25)';

  return (
    <section className="w-full py-14 lg:py-20 px-4">
      <div className="max-w-[1280px] mx-auto">

        {/* Header */}
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 border rounded-full px-5 py-2 mb-6`}
            style={{ background: accentBg, borderColor: accentBorder }}>
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: accent }} />
            <span className="text-[13px] font-medium" style={{ color: accent }}>
              Limited offer — Try Pro free for 14 days. Only $2.85 to start.
            </span>
          </div>

          <h2 className={`text-[28px] md:text-[40px] font-bold ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>
            Choose your Plan
          </h2>
          <p className={`text-[14px] md:text-[16px] mt-2 max-w-lg mx-auto ${isDark ? 'text-gray-300' : 'text-[#64748B]'}`}>
            Unlock 16 AI models, PDF tools, and custom assistants — cancel anytime.
          </p>
        </div>

        {/* Toggle */}
        <div className="flex justify-center mb-10">
          <div className="relative">
            <div className={`relative flex items-center h-[44px] w-[300px] rounded-[14px] p-[3px] border ${
              isDark ? 'bg-[#1A1A1C] border-[#2A2A2D]' : 'bg-[#F1F5F9] border-[#E2E8F0]'
            }`}>
              <div
                className={`absolute h-[38px] w-[146px] rounded-[12px] transition-all duration-300 ease-in-out border ${
                  isDark ? 'bg-[#0D2E23] border-[#28F5AB]' : 'bg-white border-[#177755] shadow-sm'
                }`}
                style={{ left: billing === 'monthly' ? '3px' : '151px' }}
              />
              {['monthly', 'yearly'].map(b => (
                <button
                  key={b}
                  className={`relative z-[1] flex-1 h-[38px] rounded-[12px] text-[14px] font-semibold bg-transparent transition-colors duration-300 capitalize ${
                    billing === b
                      ? isDark ? 'text-white' : 'text-[#177755]'
                      : isDark ? 'text-[#8A8A8A]' : 'text-[#94A3B8]'
                  }`}
                  onClick={() => setBilling(b)}
                >
                  {b.charAt(0).toUpperCase() + b.slice(1)}
                </button>
              ))}
            </div>
            <div className={`absolute -top-[12px] right-0 translate-x-1/2 px-[8px] py-[3px] rounded-full text-[10px] font-bold whitespace-nowrap ${
              isDark ? 'bg-[#28F5AB] text-[#0A0A0A]' : 'bg-[#177755] text-white'
            }`}>
              SAVE 20%
            </div>
          </div>
        </div>

        {/* Cards */}
        <div className="flex flex-col lg:flex-row gap-6 justify-center items-stretch">
          {PLANS.map((plan) => {
            const price = billing === 'yearly' ? getYearlyPrice(plan.monthlyPrice) : plan.monthlyPrice;
            const displayPrice = billing === 'monthly' && plan.trial ? formatPrice(plan.trial.price) : formatPrice(price);
            const suffix = billing === 'yearly' ? '/yr' : billing === 'monthly' && plan.trial ? `/ ${plan.trial.days} days` : '/mo';

            return (
              <div
                key={plan.name}
                className={`relative flex flex-col rounded-[24px] flex-1 max-w-[340px] w-full mx-auto lg:mx-0 border transition-all duration-200 ${
                  plan.isPopular
                    ? isDark
                      ? 'border-[#28F5AB] bg-[#0d1a14] shadow-[0_0_40px_rgba(40,245,171,0.12)]'
                      : 'border-[#177755] bg-white shadow-[0_8px_40px_rgba(23,119,85,0.15)]'
                    : isDark
                      ? 'border-[#2A2A2D] bg-[#141416]'
                      : 'border-[#E2E8F0] bg-white shadow-[0_4px_24px_rgba(0,0,0,0.06)]'
                }`}
              >
                {plan.isPopular && (
                  <div className={`absolute -top-[13px] left-1/2 -translate-x-1/2 text-[10px] font-bold px-[14px] py-[4px] rounded-full whitespace-nowrap ${
                    isDark ? 'bg-[#28F5AB] text-[#0A0A0A]' : 'bg-[#177755] text-white'
                  }`}>
                    MOST POPULAR
                  </div>
                )}

                <div className="p-[24px] flex flex-col h-full">
                  <h3 className={`text-[20px] font-semibold ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>{plan.name}</h3>
                  <p className={`text-[12px] mt-[3px] ${isDark ? 'text-[#B4B4B4]' : 'text-[#94A3B8]'}`}>
                    {billing === 'yearly' ? 'Per year' : 'Per month'}
                  </p>

                  <div className="mt-[14px]">
                    {billing === 'yearly' && (
                      <span className={`text-[13px] font-medium line-through block mb-[2px] ${isDark ? 'text-[#555]' : 'text-[#CBD5E1]'}`}>
                        {formatPrice(plan.monthlyPrice * 12)}
                      </span>
                    )}
                    <div className="flex items-baseline gap-[4px]">
                      <span className={`text-[36px] font-bold leading-[36px] ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>{displayPrice}</span>
                      <span className={`text-[14px] ${isDark ? 'text-[#B4B4B4]' : 'text-[#94A3B8]'}`}>{suffix}</span>
                    </div>
                    {billing === 'monthly' && plan.trial && (
                      <p className={`text-[12px] mt-[4px] ${isDark ? 'text-[#B4B4B4]' : 'text-[#94A3B8]'}`}>
                        then {formatPrice(plan.monthlyPrice)}/mo after trial
                      </p>
                    )}
                    {billing === 'yearly' && (
                      <div className="mt-[8px]">
                        <span className="inline-flex items-center gap-[5px] px-[10px] py-[4px] rounded-full text-[11px] font-semibold"
                          style={{ background: accentBg, color: accent, border: `1px solid ${accentBorder}` }}>
                          Just {formatPrice(Math.round(price / 12 * 100) / 100)}/mo
                        </span>
                      </div>
                    )}
                    <p className={`text-[12px] mt-[6px] ${isDark ? 'text-[#B4B4B4]' : 'text-[#64748B]'}`}>{plan.description}</p>
                  </div>

                  <div className="flex flex-col gap-[10px] mt-[18px] flex-1">
                    {plan.features.map((feature) => (
                      <div key={feature} className="flex items-start gap-[10px]">
                        <div className="flex items-center justify-center w-[18px] h-[18px] rounded-full shrink-0 mt-[1px]"
                          style={{ background: plan.isPopular ? accent : accentBg, border: plan.isPopular ? 'none' : `1px solid ${accentBorder}` }}>
                          <Check className="w-[9px] h-[9px]"
                            style={{ color: plan.isPopular ? (isDark ? '#0A0A0A' : 'white') : accent }} />
                        </div>
                        <span className={`text-[13px] leading-[1.4] ${isDark ? 'text-[#D4D4D4]' : 'text-[#475569]'}`}>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <a
                    href="https://chat.vuzo.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`mt-[20px] w-full h-[44px] rounded-full text-[14px] font-semibold flex items-center justify-center transition-all duration-200 hover:opacity-90 ${
                      plan.isPopular
                        ? isDark ? 'bg-[#28F5AB] text-[#0A0A0A]' : 'bg-[#177755] text-white shadow-md'
                        : isDark ? 'bg-[#2A2A2D] text-white' : 'bg-[#F1F5F9] text-[#0F172A] hover:bg-[#E2E8F0]'
                    }`}
                  >
                    {billing === 'monthly' && plan.trial ? 'Start 14-day trial' : 'Get it now'}
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
